import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { trackFb } from "@/lib/fbq";
import { toast } from "sonner";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { useAuth } from "@/contexts/AuthContext";
import { Captcha } from "@/pages/FeedbackReport";
import { Mail } from "lucide-react";

export default function ContactPage() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const refresh = () => api.captcha().then(setCaptcha).catch(() => toast.error("Captcha failed to load."));
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (user) {
      setName((n) => n || user.display_name || "");
      setEmail((e) => e || user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error("Please enter a valid email."); return; }
    if (!subject.trim()) { toast.error("Please enter a subject."); return; }
    if (message.trim().length < 10) { toast.error("Message needs at least 10 characters."); return; }
    setBusy(true);
    try {
      await api.submitContact({ name, email: email.trim(), subject, message, captcha_id: captcha.captcha_id, captcha_answer: answer });
      trackFb("Contact");
      setSent(true);
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Couldn't send your message.");
      refresh(); setAnswer("");
    } finally { setBusy(false); }
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-2xl mx-auto px-6 py-14" data-testid="contact-page">
        <div className="text-veil">
        <div className="text-sm uppercase tracking-[0.32em] font-bold mb-3"><span className="text-[#FF1A82]">Welp</span> <span className="text-[#5A5A5A]">·</span> Contact</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A]">Get in <span className="italic text-[#10B981]">touch.</span></h1>
        <p className="mt-4 text-[#1A1A1A] text-lg font-semibold">Account trouble, questions, press, anything — no account needed. You'll get a confirmation email right away.</p>
        </div>

        {sent ? (
          <div className="mt-10 rounded-3xl bg-white/70 backdrop-blur-lg border-2 border-[#FF1A82] p-7 shadow-lg" data-testid="contact-sent">
            <Mail className="w-8 h-8 text-[#FF1A82]" />
            <h2 className="font-heading text-3xl font-bold mt-3">Message sent.</h2>
            <p className="mt-2 text-[#1A1A1A] font-semibold">Check your inbox for a confirmation. We'll reply to {email} if a response is needed.</p>
            <Link to="/" className="mt-5 inline-block px-5 py-3 rounded-full bg-[#FF1A82] text-white font-bold">Back home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
            <input data-testid="contact-name" required maxLength={100} value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-semibold" />
            <input data-testid="contact-email" required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-semibold" />
            <input data-testid="contact-subject" required maxLength={150} value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#10B981] font-semibold" />
            <textarea data-testid="contact-message" required minLength={10} maxLength={2000} rows={6} value={message} onChange={e => setMessage(e.target.value)} placeholder="How can we help?"
              className="w-full bg-white/60 border border-black/10 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] font-semibold resize-none" />
            <div className="text-xs text-[#5A5A5A] text-right font-semibold">{message.length}/2000</div>
            <Captcha captcha={captcha} refresh={refresh} answer={answer} setAnswer={setAnswer} />
            <button disabled={busy} data-testid="contact-submit" className="w-full px-5 py-3 rounded-full bg-[#10B981] text-white font-bold hover:brightness-110 disabled:opacity-60">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </section>
    </PageBg>
  );
}
